import { useStateManagementSlice } from '@gef-ui/context-defaults';
import isNotEmpty from "ramda-extension/es/isNotEmpty";
import noop from "ramda-extension/es/noop";
import { useContext, useEffect, useState } from 'react';
import { ApmContext } from '../context';
import { initApmWithAccessToken } from '../utils';
const stateSlice = 'Apm';
/**
 * Provides an initialization of APM agent with configuration params supplied from redux
 *
 * @documented
 * @category APM
 * @import import { useInitializeApmInstance } from '@gef-ui/apm/hooks'
 * @param {Function(APM_INSTANCE)} onReady - Called when APM instance is ready.
 * @returns {Object} apm - APM RUM agent instance. Before initialization value will be null
 * @example
 *
 * const apmInstace = useInitializeApmInstance((apm) => {
 * 		apm.setUserContext({ id: 'random' });
 * });
 *
 */ export const useInitializeApmInstance = (onReady = noop)=>{
    const [apmInstance, setApmInstace] = useState(null);
    const { useConfig, useGetAccessToken } = useStateManagementSlice(stateSlice);
    const apmConfig = useConfig();
    const getAccessToken = useGetAccessToken();
    useEffect(()=>{
        if (isNotEmpty(apmConfig)) {
            (async ()=>{
                const apm = initApmWithAccessToken(getAccessToken, apmConfig);
                onReady(apm);
                setApmInstace(apm);
            })();
        }
    }, [
        onReady,
        apmConfig,
        getAccessToken
    ]);
    return apmInstance;
};
/**
 * Automatically initialized/reinitialized APM user context when userLogin has changed or initialized
 *
 * @documented
 * @category APM
 * @import import { useInitializeApmUserContext } from '@gef-ui/apm/hooks'
 * @param {APM_INSTANCE} apm - APM instance object
 * @param {Function(REDUX_STATE)} userLoginSelector - UserLogin selector that will be used as APM user ID. Make sure that selector is memoized.
 * @example
 *
 * useInitializeApmUserContext(apm, userLoginSelector);
 *
 */ export const useInitializeApmUserContext = (apm, userLoginSelector)=>{
    const { useUserLogin } = useStateManagementSlice(stateSlice);
    const userLogin = useUserLogin(userLoginSelector);
    useEffect(()=>{
        if (apm && userLogin) {
            apm.setUserContext({
                id: userLogin
            });
        }
    }, [
        apm,
        userLogin
    ]);
};
/**
 * Provides an easy access to current apm instance throught context API when using ApmProvider Component
 *
 * @documented
 * @category APM
 * @import import { useApmInstance } from '@gef-ui/apm/hooks'
 * @returns {Object} apm - APM RUM agent instance. Before initialization or when React context is not available value will be null
 * @example
 *
 * const apmInstace = useApmInstance();
 *
 */ export const useApmInstance = ()=>{
    const apmInstance = useContext(ApmContext);
    return apmInstance;
};
